<template>
  <v-card outlined>
    <!-- Search Condition -->
    <v-container class="px-1 py-1" fluid>
      <v-card-title class="pt-2 pb-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <v-card-text>
        <ValidationObserver ref="observer" v-slot="{ invalid }">
          <v-row>
            <v-col cols="4">
              <PartCodeInput v-if="isEpson" v-model="searchCondition.partCd" required :disabled="propsExists" />
              <SupplierPartInput
                v-else
                v-model="searchCondition.partCd"
                :supplierCd="''"
                isSearchByLoginCompany
                required
                :disabled="propsExists"
              />
            </v-col>
            <v-col cols="4">
              <CompanyInput
                :label="$t('ELE0134')"
                v-if="searchCondition.partCd == ''"
                v-model="searchCondition.supplierCd"
                required
                disabled
              />
              <PartSupplierInput
                :label="$t('ELE0134')"
                v-else
                v-model="searchCondition.supplierCd"
                :partCd="searchCondition.partCd"
                required
                validOnly
                :disabled="propsExists"
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <span d-flex flex-row space-between>
                <BtnSearch @click="onClickSearch" :disabled="propsExists || invalid || isFieldAllEmpty" />
                <BtnReset class="mx-4" @click="onClickResetForm" :disabled="!propsExists" />
              </span>
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-card-text>
    </v-container>
    <!-- Part Information -->
    <v-container v-if="isSearchClicked" fluid>
      <v-card outlined>
        <v-card-title>
          {{ $t('ELE0018') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0290')" v-model="partInfo.inspFilteringGroup" class="large-text" />
            </v-col>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0339')" v-model="partInfo.inspLevel" />
            </v-col>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0069') + ' ' + $t('ELE0532')" v-model="partInfo.xrfQty" />
            </v-col>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0534')" v-model="partInfo.postProcess" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2">
              <v-row>
                <v-col cols="auto" class="d-flex justify-start align-start">
                  <span style="font-size: 100%; color: black">
                    {{ $t('ELE0148') }}
                  </span>
                </v-col>
                <v-col cols="auto" class="d-flex justify-start align-center">
                  <span v-if="partInfo.isRequiredXrfOriginal" class="required-xrf">
                    {{ partInfo.isRequiredXrf }}
                  </span>
                  <span v-else class="not-required-xrf">
                    {{ partInfo.isRequiredXrf }}
                  </span>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="1" class="d-flex justify-end">
              <BtnHistoryOpen @click="onClickXrfHistory" />
            </v-col>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0203')" v-model="partInfo.xrfLastDatetime" />
            </v-col>
            <v-col cols="3">
              <TextFieldRef :label="$t('ELE0254')" v-model="partInfo.xrfExpiredDate" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
    <!-- Insp Info -->
    <v-container v-if="isSearchClicked" fluid>
      <!-- OQC Inspection Results -->
      <v-card outlined>
        <v-card-title>
          {{ $t('ELE0257') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-card-title>
              {{ $t('ELE0072') + $t('ELE0497') + ' ' + $t('ELE0531') }}
            </v-card-title>
            <v-col cols="3">
              <SelectSingleInput
                :items="rangeItems"
                :label="$t('ELE0535')"
                v-model="searchConditionOQC.range"
                itemText="name"
                itemValue="value"
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <BtnSearch @click="onClickSearchOQC" />
            </v-col>
          </v-row>
          <v-row>
            <v-data-table
              :headers="oqcHeaders"
              :items="oqcItems"
              hide-default-footer
              sort-by="indexCreateDatetime"
              :sort-desc="[true]"
              dense
            >
              <template v-slot:item.action="{ item }">
                <IconInquiry @click="onClickInspSub(item)" />
              </template>
              <template v-slot:item.inspIndexNo="{ item }">
                <BtnLink :label="item.inspIndexNo" @click="onClickInspIndexNo(item)" />
              </template>
              <template v-slot:[`item.inspResultCd`]="{ item }">
                <td
                  class="v-data-table__divider"
                  :style="{ 'background-color': getColorInspResult(item.inspResultCd) }"
                >
                  {{ getInspResultName('InspResultCd.', item.inspResultCd, item.isOutCl) }}
                </td>
              </template>
              <template v-slot:item.indexCreateDatetime="{ item }">
                {{ toLocalTime(item.indexCreateDatetime) }}
              </template>
              <template v-slot:item.inspEndDatetime="{ item }">
                {{ toLocalTime(item.inspEndDatetime) }}
              </template>
              <template v-slot:[`item.inspStatusCd`]="{ item }">
                {{ getEnumlang('InspIndex.InspStatusCd.', item.inspStatusCd) }}
              </template>
              <template v-slot:[`item.inspWfChecked`]="{ item }">
                <span v-if="item.inspWfChecked !== null">
                  {{ $t('ApproveStatusCd.' + item.inspWfChecked) }}
                </span>
              </template>
            </v-data-table>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- IQC Inspection Results -->
      <v-card outlined>
        <v-card-text></v-card-text>
        <v-card-text>
          <v-row>
            <v-card-title>
              {{ $t('ELE0073') + $t('ELE0497') + ' ' + $t('ELE0531') }}
            </v-card-title>
            <v-col cols="3">
              <SelectSingleInput
                :items="rangeItems"
                :label="$t('ELE0253') + $t('ELE0201')"
                v-model="searchConditionIQC.range"
                itemText="name"
                itemValue="value"
              />
            </v-col>
            <v-col cols="3">
              <TextFieldInput v-model="searchConditionIQC.dnpoNo" :label="$t('ELE0368')" />
            </v-col>
            <v-col class="d-flex justify-end">
              <BtnSearch @click="onClickSearchIQC" />
            </v-col>
          </v-row>
          <v-row>
            <v-data-table
              :headers="iqcHeaders"
              :items="iqcItems"
              hide-default-footer
              sort-by="acceptDatetime"
              :sort-desc="[true]"
              dense
            >
              <template v-slot:item.action="{ item }">
                <IconInquiry @click="onClickInspSub(item)" />
              </template>
              <template v-slot:item.inspIndexNo="{ item }">
                <BtnLink :label="item.inspIndexNo" @click="onClickInspIndexNo(item)" />
              </template>
              <template v-slot:[`item.inspResultCd`]="{ item }">
                <td
                  class="v-data-table__divider"
                  :style="{ 'background-color': getColorInspResult(item.inspResultCd) }"
                >
                  {{ getInspResultName('InspResultCd.', item.inspResultCd, item.isOutCl) }}
                </td>
              </template>
              <template v-slot:item.acceptDatetime="{ item }">
                {{ toLocalTime(item.acceptDatetime) }}
              </template>
              <template v-slot:item.inspStartDatetime="{ item }">
                {{ toLocalTime(item.inspStartDatetime) }}
              </template>
              <template v-slot:item.inspEndDatetime="{ item }">
                {{ toLocalTime(item.inspEndDatetime) }}
              </template>
              <template v-slot:[`item.inspStatusCd`]="{ item }">
                {{ getEnumlang('InspIndex.InspStatusCd.', item.inspStatusCd) }}
              </template>
              <template v-slot:[`item.inspWfChecked`]="{ item }">
                <span v-if="item.inspWfChecked !== null">
                  {{ $t('ApproveStatusCd.' + item.inspWfChecked) }}
                </span>
              </template>
            </v-data-table>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- 4M -->
      <v-card outlined>
        <v-card-text></v-card-text>
        <v-card-text>
          <v-row>
            <v-card-title>
              {{ $t('ELE0491') + $t('ELE0504') + ' ' + $t('ELE0531') }}
            </v-card-title>
          </v-row>
          <v-row>
            <v-data-table
              :headers="mmmmHeaders"
              :items="mmmmItems"
              hide-default-footer
              sort-by="issueDate"
              :sort-desc="[true]"
              dense
            >
              <template v-slot:item.action="{ item }">
                <IconInquiry @click="onClickInquiryMmmm(item.mmmmNo)" />
              </template>
              <template v-slot:item.issueDate="{ item }">
                {{ toLocalTime(item.issueDate) }}
              </template>
              <template v-slot:[`item.statusCd`]="{ item }">
                {{ $t(`MmmmApprovalStatusCd.${item.statusCd}`) }}
              </template>
            </v-data-table>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- SA -->
      <v-card outlined>
        <v-card-text></v-card-text>
        <v-card-text>
          <v-row>
            <v-card-title>
              {{ $t('ELE0291') + $t('ELE0504') + ' ' + $t('ELE0531') }}
            </v-card-title>
          </v-row>
          <v-row>
            <v-data-table
              :headers="saHeaders"
              :items="saItems"
              hide-default-footer
              sort-by="issueDate"
              :sort-desc="[true]"
              dense
            >
              <template v-slot:item.action="{ item }">
                <IconInquiry @click="onClickInquirySa(item.saNo)" />
              </template>
              <template v-slot:[`item.statusCd`]="{ item }">
                {{ $t(`SaApprovalStatusCd.${item.statusCd}`) }}
              </template>
              <template v-slot:item.inspClassCd="{ item }">
                {{ $t(`InspClassCd.${item.inspClassCd}`) }}
              </template>
            </v-data-table>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
    <SearchInspLotSubDialog
      v-if="subDialog"
      :dialog.sync="subDialog"
      :inspIndexNo="selectedSubItems.inspIndexNo"
      :manufacturerCompanyName="selectedSubItems.manufacturerName"
      :inspCompanyName="selectedSubItems.inspCompanyName"
      :lotDate="selectedSubItems.lotDate"
      :lotNo="selectedSubItems.lotNo"
      :partCd="selectedSubItems.partCd"
      :inspStatusCd="selectedSubItems.inspStatusCd"
      @close="subDialog = false"
    />
    <XrfHistoryDialog
      v-if="xrfHistoryDialog"
      :dialog.sync="xrfHistoryDialog"
      :part="xrfHistoryItem.partCd"
      :supplier="xrfHistoryItem.supplier"
      :partCd="xrfHistoryItem.partCd"
      :manufacturerCd="xrfHistoryItem.manufacturerCd"
    />
    <ApprovalInspDataListDialog
      v-if="selectedInspIndexItem && inspDtlRefDialog"
      :dialog.sync="inspDtlRefDialog"
      isInquiry
      inspDataViewOnly
      :inspIndexNo="selectedInspIndexItem.inspIndexNo"
      :inspCompanyCd="selectedInspIndexItem.inspCompanyCd"
      :lotNo="selectedInspIndexItem.lotNo"
      :partCd="this.searchCondition.partCd"
      :partName="this.partName"
      :lotDate="selectedInspIndexItem.lotDate"
      :inspCat1="selectedInspIndexItem.inspCat1"
      :inspCat2="selectedInspIndexItem.inspCat2"
    />
    <v-dialog v-if="mmmmDialog" v-model="mmmmDialog" scrollable persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ $t('ELE0491') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="mmmmDialog = false" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <MmmmRequestForm :propsMmmmNo="selectedMmmmNo" @close=";(mmmmDialog = false), onClickSearch()" isModal />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-if="saInquiryDialog" v-model="saInquiryDialog" scrollable persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ $t('ELE0291') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="saInquiryDialog = false" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <SaRequestForm :propsSaNo="selectedSaNo" @close=";(saInquiryDialog = false), onClickSearch()" isModal />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import StringUtils from '@/mixin/string-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import PartSupplierInput from '@/components/atoms/select/PartSupplierInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import { InspInfoRange, InspResultCd } from '@/enum'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import BtnLink from '@/components/atoms/base/button/BtnLink'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import { ValidationObserver } from 'vee-validate'
import SearchInspLotSubDialog from '@/components/organisms/terminal/dialog/SearchInspLotSubDialog'
import XrfHistoryDialog from '@/components/organisms/terminal/dialog/XrfHistoryDialog'
import Btn from '@/components/atoms/base/button/Btn'
import MmmmRequestForm from '@/components/organisms/terminal/form/MmmmRequestForm'
import SaRequestForm from '@/components/organisms/terminal/form/SaRequestForm'
import ApprovalInspDataListDialog from '@/components/organisms/terminal/dialog/ApprovalInspDataListDialog.vue'
import cloneDeep from 'lodash/cloneDeep'
import BtnReset from '@/components/atoms/button/BtnReset'
import BtnHistoryOpen from '@/components/atoms/button/BtnHistoryOpen'

export default {
  name: 'SearchInspPrerequisiteForm',
  mixins: [APIUtils, Message, Loading, DateUtils, StringUtils],
  components: {
    PartCodeInput,
    TextFieldInputWithDatePicker,
    BtnSearch,
    CompanyInput,
    SupplierPartInput,
    PartSupplierInput,
    TextFieldRef,
    SelectSingleInput,
    IconInquiry,
    BtnLink,
    TextFieldInput,
    ValidationObserver,
    SearchInspLotSubDialog,
    XrfHistoryDialog,
    Btn,
    MmmmRequestForm,
    SaRequestForm,
    ApprovalInspDataListDialog,
    BtnReset,
    BtnHistoryOpen,
  },
  props: {
    propsMmmmNo: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      oqcItems: [],
      oqcHeaders: [
        { text: this.$t('ELE0307'), value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0137'), value: 'inspIndexNo', divider: true },
        { text: this.$t('ELE0181'), value: 'inspResultCd', divider: true },
        { text: this.$t('ELE0049'), value: 'lotNo', divider: true },
        { text: this.$t('ELE0535'), value: 'indexCreateDatetime', divider: true },
        { text: this.$t('ELE0210'), value: 'inspEndDatetime', divider: true },
        { text: this.$t('ELE0139'), value: 'inspStatusCd', divider: true },
        { text: this.$t('ELE0217'), value: 'inspWfChecked', divider: true },
      ],
      iqcItems: [],
      iqcHeaders: [
        { text: this.$t('ELE0307'), value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0137'), value: 'inspIndexNo', divider: true },
        { text: this.$t('ELE0181'), value: 'inspResultCd', divider: true },
        { text: this.$t('ELE0049'), value: 'lotNo', divider: true },
        { text: this.$t('ELE0368'), value: 'dnNoPoNo', divider: true },
        { text: this.$t('ELE0055'), value: 'acceptDatetime', divider: true },
        { text: this.$t('ELE0358'), value: 'inspStartDatetime', divider: true },
        { text: this.$t('ELE0210'), value: 'inspEndDatetime', divider: true },
        { text: this.$t('ELE0139'), value: 'inspStatusCd', divider: true },
        { text: this.$t('ELE0217'), value: 'inspWfChecked', divider: true },
      ],
      mmmmItems: [],
      mmmmHeaders: [
        { text: this.$t('ELE0491'), value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0052'), value: 'mmmmNo', divider: true },
        { text: this.$t('ELE0218'), value: 'statusCd', divider: true },
        { text: this.$t('ELE0446'), value: 'reasonName', divider: true },
        { text: this.$t('ELE0174'), value: 'mmmmQty', divider: true },
        { text: this.$t('ELE0223'), value: 'issueDate', divider: true },
        { text: this.$t('ELE0279'), value: 'currentCheckedBy', divider: true },
      ],
      saItems: [],
      saHeaders: [
        { text: this.$t('ELE0291'), value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0184'), value: 'saNo', divider: true },
        { text: this.$t('ELE0218'), value: 'statusCd', divider: true },
        { text: this.$t('ELE0156'), value: 'inspClassCd', divider: true },
        { text: this.$t('ELE0223'), value: 'issueDate', divider: true },
        { text: this.$t('ELE0254'), value: 'endDate', divider: true },
        { text: this.$t('ELE0279'), value: 'currentCheckedBy', divider: true },
      ],
      searchCondition: {
        partCd: '',
        supplierCd: '',
      },
      searchConditionOQC: {
        range: '1',
      },
      searchConditionIQC: {
        range: '1',
        dnpoNo: '',
      },
      partInfo: {
        partCdName: '',
        supplierCdName: '',
        inspFilteringGroup: '',
        inspLevel: '',
        isRequiredXrf: '',
        isRequiredXrfOriginal: '',
        xrfLastDateTime: '',
        xrfQty: '',
        postProcess: '',
        xrfExpiredDate: '',
      },
      xrfSearchList: [],
      xrf_need_flag: '',
      isSearchClicked: false,
      selectedSubItems: null,
      selectedInspIndexItem: null,
      inspDtlRefDialog: false,
      subDialog: false,
      xrfHistoryDialog: false,
      partName: '',
      supplierName: '',
      selectedMmmmNo: '',
      mmmmDialog: false,
      saInquiryDialog: false,
      selectedSaNo: '',
      propsExists: false,
    }
  },
  computed: {
    isFieldAllEmpty: {
      get() {
        return this.isAllEmpty(this.searchCondition.partCd, this.searchCondition.supplierCd)
      },
    },
    isEpson: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    rangeItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(InspInfoRange).map((item) => {
          return { name: item + ' : ' + this.$t(`InspInfoRange.${item}`), value: item }
        })
      },
    },
  },
  methods: {
    getEnumlang(key, value) {
      // langのenumからメッセージ取得
      let lang = ''
      if (value) {
        lang = this.$t(key + value)
      }
      return lang
    },
    getColorInspResult(inspResultCd) {
      // 検査結果に対応する色設定
      let color = ''
      if (inspResultCd == InspResultCd.OK) {
        color = '#EDEFFD'
      } else if (inspResultCd == InspResultCd.SA_OK) {
        color = '#FCF4A1'
      } else if (inspResultCd == InspResultCd.NG) {
        color = '#F8CED2'
      }
      return color
    },
    onClickXrfHistory() {
      this.xrfHistoryItem = {
        part: this.partName,
        supplier: this.supplierName,
        partCd: this.searchCondition.partCd,
        manufacturerCd: this.searchCondition.supplierCd,
      }
      this.xrfHistoryDialog = true
    },
    onClickInspIndexNo(item) {
      this.selectedInspIndexItem = {
        ...cloneDeep(item),
        lotDate: this.toLocalDate(item.inspCreateDatetime),
      }
      this.inspDtlRefDialog = true
    },
    onClickInspSub(item) {
      this.selectedSubItems = {
        inspIndexNo: item.inspIndexNo,
        manufacturerName: this.supplierName,
        inspCompanyName: item.inspCompanyName,
        lotDate: this.toLocalDate(item.acceptDatetime),
        inspStatusCd: item.inspStatusCd,
        lotNo: item.lotNo,
        partCd: this.searchCondition.partCd,
      }
      this.subDialog = true
    },
    onClickInquiryMmmm(mmmmNo) {
      this.selectedMmmmNo = mmmmNo
      this.mmmmDialog = true
    },
    onClickInquirySa(saNo) {
      this.selectedSaNo = saNo
      this.saInquiryDialog = true
    },
    onClickResetForm() {
      this.searchCondition.inspIndexNo = ''
      this.searchCondition.supplierCd = ''
      this.resetForm()
    },
    resetForm() {
      this.searchCondition.partCd = ''
      this.searchCondition.supplierCd = ''
      this.partInfo.partCdName = ''
      this.partInfo.supplierCdName = ''
      this.partInfo.inspFilteringGroup = ''
      this.partInfo.inspLevel = ''
      this.partInfo.isRequiredXrf = ''
      this.partInfo.xrfLastDatetime = ''
      this.partInfo.xrfQty = ''
      this.partInfo.postProcess = ''
      this.isSearchClicked = false
      this.oqcItems = []
      this.iqcItems = []
      this.mmmmItems = []
      this.saItems = []
      this.propsExists = false
    },
    async onClickSearch() {
      this.startLoading()
      try {
        if (!(await this.$refs.observer.validate()) || this.isFieldAllEmpty) return

        const params = { ...this.searchCondition }
        const element = await API.get('/xrfs/partInfo', params)
        this.partInfo.partCdName = `${this.searchCondition.partCd} : ${element.partName}`
        this.partInfo.supplierCdName = `${this.searchCondition.supplierCd} : ${element.supplierName}`
        this.partInfo.inspFilteringGroup = element.inspFilteringGroup
        if (element.isRequiredXrf === true) {
          this.partInfo.isRequiredXrf = this.$t(`IsRequiredXrf.${element.isRequiredXrf}`)
        } else if (element.isRequiredXrf === false) {
          this.partInfo.isRequiredXrf = this.$t(`IsRequiredXrf.${element.isRequiredXrf}`)
        } else {
          this.partInfo.isRequiredXrf = ''
        }
        this.partInfo.isRequiredXrfOriginal = element.isRequiredXrf
        this.partInfo.xrfLastDatetime = element.latestXrfInspResult
        this.partInfo.inspLevel = element.inspLevel
        this.partInfo.xrfQty = element.xrfQty
        this.partInfo.postProcess = element.postProcess ? this.$t('XrfPostProcessingCd.' + element.postProcess) : ''
        this.partInfo.xrfExpiredDate = element.xrfExpiredDate
        this.isSearchClicked = true
        this.partName = element.partName
        this.supplierName = element.supplierName

        const params_oqc = {
          manufacturerCd: this.searchCondition.supplierCd,
          partCd: this.searchCondition.partCd,
          rangeFrom: this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 1))),
          rangeTo: this.toLocalDate(new Date()),
        }
        this.oqcItems = await API.get('/inspIndex/inspIndexesOQC', params_oqc)

        const params_iqc = {
          manufacturerCd: this.searchCondition.supplierCd,
          partCd: this.searchCondition.partCd,
          rangeCd: this.searchConditionIQC.range,
          rangeFrom: this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 1))),
          rangeTo: this.toLocalDate(new Date()),
        }
        this.iqcItems = await API.get('/inspIndex/inspIndexesIQC', params_iqc)

        const params_mmmm = {
          manufacturerCd: this.searchCondition.supplierCd,
          partCd: this.searchCondition.partCd,
        }
        this.mmmmItems = await API.get('/mmmm/partSupplier', params_mmmm)

        const params_sa = {
          partCd: this.searchCondition.partCd,
          manufacturerCd: this.searchCondition.supplierCd,
        }
        this.saItems = await API.get('/sa/partSupplier', params_sa)
        this.propsExists = true
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0170') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickSearchOQC() {
      try {
        this.startLoading()
        let range_from
        switch (this.searchConditionOQC.range) {
          case '1':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 1)))
            break
          case '2':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 3)))
            break
          case '3':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 6)))
            break
          case '4':
            range_from = this.toLocalDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)))
            break
          default:
            range_from = ''
        }
        const params = {
          userCompanyCd: this.searchCondition.supplierCd,
          partCd: this.searchCondition.partCd,
          rangeFrom: range_from,
          rangeTo: this.toLocalDate(new Date()),
        }
        this.oqcItems = await API.get('/inspIndex/inspIndexesOQC', params)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickSearchIQC() {
      try {
        this.startLoading()
        let range_from
        switch (this.searchConditionIQC.range) {
          case '1':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 1)))
            break
          case '2':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 3)))
            break
          case '3':
            range_from = this.toLocalDate(new Date(new Date().setMonth(new Date().getMonth() - 6)))
            break
          case '4':
            range_from = this.toLocalDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)))
            break
          default:
            range_from = ''
        }
        const params = {
          userCompanyCd: this.searchCondition.supplierCd,
          partCd: this.searchCondition.partCd,
          rangeFrom: range_from,
          rangeTo: this.toLocalDate(new Date()),
          dnpoNo: this.searchConditionIQC.dnpoNo,
        }
        this.iqcItems = await API.get('/inspIndex/inspIndexesIQC', params)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
  },
  mounted() {},
}
</script>

<style scoped>
.required-xrf {
  font-size: 300%;
  color: red;
  font-weight: bold;
}
.not-required-xrf {
  font-size: 200%;
}
</style>
